import {AnswerStatus, ActionStatusType} from '@domain/pom'
import {ApprovalStatus} from '@domain/shared'
import {formatTimeZoneDate} from '@hconnect/uikit'
import {MarkdownText} from '@hconnect/uikit/src/lib2'
import {Avatar, Box} from '@mui/material'
import {ApproversBanner, ApproversList} from '@widgets/common-approvers'
import i18next from 'i18next'
import React, {FC, useCallback, useState} from 'react'
import {useTranslation} from 'react-i18next'

import {FEATURE_NAMESPACE} from '../../consts'
import {useEditAction, useTimeZone, usePlantUpmId, useActionApproval} from '../../hooks'
import {ActionItem} from '../../types'
import {getInitials} from '../../utils'
import {StatusField} from '../actionDialog/StatusField'
import {MoveQuestionToDone} from '../actionsCompleted/MoveQuestionToDone'
import {Attachments} from '../common'

import {ActionComments} from './ActionComments'
import {ActionConnectedToSectionLink} from './ActionConnectedToSectionLink'
import {ActionDetailsSection} from './ActionDetailsSection'
import {GcbDetailsSection} from './GcbDetailsSection'
import {hasMipElement, MipDetailsSection} from './MipDetailsSection'

const getSourceRowMb = (action: ActionItem): number =>
  action.costLeadership || action.questionId || hasMipElement(action) ? 3 : 0

type ActionsDetailsProps = {
  action: ActionItem
  onChange: (action: ActionItem) => void
  questionAnswer?: AnswerStatus
  showApprovers?: boolean
}

export const ActionsDetails: FC<ActionsDetailsProps> = ({
  action,
  onChange,
  questionAnswer,
  showApprovers = false
}) => {
  const timeZone = useTimeZone()
  const plantId = usePlantUpmId()
  const {t} = useTranslation(FEATURE_NAMESPACE)

  const [showMoveQuesDialog, setShowMoveQuesDialog] = useState<boolean>(false)
  const [pendingAction, setPendingAction] = useState<ApprovalStatus>()

  const {mutate: editAction} = useEditAction(({item, showMoveQuestToDone}) => {
    onChange(item)
    showMoveQuestToDone && setShowMoveQuesDialog(true)
  })
  const {mutate: updateStatus, isPending: isLoading} = useActionApproval({
    plantId,
    concurrencyStamp: action.concurrencyStamp
  })
  const onStatusChange = (status: ApprovalStatus) => {
    setPendingAction(status)
    updateStatus(
      {actionId: action.id, approvalStatus: status},
      {
        onSettled: () => setPendingAction(undefined)
      }
    )
  }

  const handleStatusChange = useCallback(
    (status: ActionStatusType) => {
      if (status !== action.status) {
        editAction({actionItem: {...action, status}, questionAnswer})
      }
    },
    [action, editAction, questionAnswer]
  )

  const needsApproval = showApprovers && !!action.approvers?.length
  const sourceRowMb = getSourceRowMb(action)

  return (
    <>
      <Box mb={3} sx={{maxWidth: 150}}>
        <StatusField
          value={action.status}
          onChange={handleStatusChange}
          needsApproval={needsApproval}
        />
      </Box>

      <Box display="flex" gap={2}>
        <Box flex={1}>
          <ActionDetailsSection
            title={t('actionDetails.source')}
            mb={sourceRowMb}
            value={t(`actionSource.${action.source}`, '-')}
          />
        </Box>
        <Box flex={1}>
          <ActionDetailsSection
            title={t('actionDetails.costLeadership')}
            mb={sourceRowMb}
            data-test-id="action-details-cost-leadership"
            value={t(`actionDetails.costLeadershipValues.${action.costLeadership ? 'yes' : 'no'}`)}
          />
        </Box>
      </Box>

      <MipDetailsSection action={action} />

      <GcbDetailsSection action={action} />

      {needsApproval && (
        <ApproversBanner
          plantId={plantId}
          approvers={action.approvers}
          eventId={action.id}
          onStatusChange={onStatusChange}
          pendingAction={pendingAction}
          isLoading={isLoading}
        />
      )}

      {needsApproval && <ApproversList approvers={action.approvers} />}

      {needsApproval && action.notifyAt && (
        <ActionDetailsSection
          title={t('actionDetails.notifyAt')}
          value={formatTimeZoneDate(action.notifyAt, timeZone, 'll', i18next.language)}
        />
      )}

      <Box mt={2}>{action.description && <MarkdownText>{action.description}</MarkdownText>}</Box>

      <ActionDetailsSection
        title={t('actionDetails.assignee')}
        value={
          <Box display="flex" alignItems="center">
            {action.assignee && (
              <Avatar sx={{mr: 1, width: 20, height: 20, fontSize: 11}}>
                {getInitials(action.assignee?.name)}
              </Avatar>
            )}
            {action.assignee ? action.assignee.name : '-'}
          </Box>
        }
      />

      <ActionDetailsSection
        title={t('actionDetails.dueDate')}
        value={formatTimeZoneDate(action.dueDate, timeZone, 'll', i18next.language)}
      />

      {action.questionId && action.section?.id && <ActionConnectedToSectionLink action={action} />}

      <Attachments attachedFiles={action.attachments} />

      <ActionComments actionId={action.id} />

      {showMoveQuesDialog && (
        <MoveQuestionToDone actionItem={action} onClose={() => setShowMoveQuesDialog(false)} />
      )}
    </>
  )
}
